import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { Profile, SubscriptionPlan } from "@/integrations/supabase/types/subscription";
import { CurrentSubscription } from "./subscription/CurrentSubscription";
import { PlanCard } from "./subscription/PlanCard";
import { TokenTopUpCard } from "./subscription/TokenTopUpCard";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

interface SubscriptionCardProps {
  profile: Profile;
  loading: boolean;
  onSubscribe: (priceId: string) => Promise<void>;
  onSync: () => Promise<void>;
}

export function SubscriptionCard({ profile, loading, onSubscribe, onSync }: SubscriptionCardProps) {
  const { data: subscriptionPlans, isLoading: plansLoading } = useQuery({
    queryKey: ["subscriptionPlans"],
    queryFn: async () => {
      console.log('Fetching subscription plans...');
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("*")
        .in('price_id', ['price_1QeOICL8jiTMrRAK7kVqP25L', 'price_1QeOHnL8jiTMrRAKPxqazhoM'])
        .order('price_amount', { ascending: true });
      
      if (error) {
        console.error('Error fetching subscription plans:', error);
        throw error;
      }

      console.log('Fetched subscription plans:', data);
      return data as SubscriptionPlan[];
    },
  });

  return (
    <Card className="w-full">
      <CardHeader className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1.5">
            <CardTitle className="text-lg md:text-xl">Medlemskap</CardTitle>
            <CardDescription className="text-sm">
              Velg medlemskapstype som passer dine behov
            </CardDescription>
          </div>
          <Button
            variant="outline"
            onClick={onSync}
            disabled={loading}
            className="w-full md:w-auto flex items-center justify-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Synkroniser</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-4 md:p-6 space-y-6">
        <CurrentSubscription profile={profile} />
        {plansLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid gap-6 md:grid-cols-2">
              {subscriptionPlans?.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  loading={loading}
                  onSubscribe={onSubscribe}
                  isCurrentPlan={plan.id === profile.subscription_plan_id}
                />
              ))}
            </div>
            <TokenTopUpCard profile={profile} loading={loading} />
          </>
        )}
      </CardContent>
    </Card>
  );
}